import ERROR_CODES, { type ErrorCode } from './apiErrorCodes';

// Shared so both consultation-request flows (credits + SMB) surface identical copy
// for the stage gate — see RequestConsultation and SMBConsultationRequest.
export const EXPERT_REQUEST_NOT_ACTIVE_MESSAGE =
  'This expert request stage isn’t Active, so new calls can’t be requested. Update the stage to proceed.';

// ENG-5322. Shared for the same reason: both flows show it as a warning and close.
export const UNREGISTERED_EXPERT_REQUEST_MESSAGE =
  "This expert hasn't joined yet, so no call was booked. We've emailed them about your request.";

// ENG-5831. A retry from the same network fails the same way, so the copy sends the
// user elsewhere rather than asking them to try again. Exported for the login forms'
// handleCredentialsMissing, which must always have copy once it has reported the case.
export const CREDENTIALS_MISSING_MESSAGE =
  'Your network may be blocking sign-in. Please try another network or device.';

// ENG-6310. What to do about an expired invitation link, shown under the page
// title whether the api reported the expiry or the page was left open past it.
export const INVITATION_BATCH_EXPIRED_NEXT_STEP =
  "Nothing was sent. The experts are still on the request: ask Claude to prepare the invitations again and you'll get a new link.";
// ENG-6310. Its counterpart under the title of cancelled invitations.
export const INVITATION_BATCH_CANCELLED_NEXT_STEP =
  'Nothing was sent. To invite these experts later, ask Claude to prepare the invitations again.';

// ENG-6310. Exported for the invitations page's SendFailed story.
export const INVITATION_BATCH_DAILY_LIMIT_MESSAGE =
  'Sending these would take you over your limit of invitations in 24 hours, so nothing was sent. Try again later, or ask Claude to prepare a smaller batch.';

export const ERROR_CODE_DEFAULT_MESSAGES: Partial<Record<ErrorCode, string>> = {
  // ENG-5670. The api refuses this type for accounts that have consulting
  // engagements. The api's own message states the rule; this one also names the
  // alternative, which is the part a customer needs. Read by notifyCreateError in
  // the ER wizard.
  [ERROR_CODES.EXPERT_REQUEST_ER_TYPE_NOT_AVAILABLE]:
    'Scoped engagements are handled through engagements for this account, so this request type is not available here.',
  [ERROR_CODES.EXPERT_REQUEST_NOT_ACTIVE]: EXPERT_REQUEST_NOT_ACTIVE_MESSAGE,
  // ENG-6310. Staff-facing: only the staff member who prepared the invitations
  // reaches the page that raises these. Each says whether anything was sent;
  // for an expiry the page adds INVITATION_BATCH_EXPIRED_NEXT_STEP under its title.
  [ERROR_CODES.INVITATION_BATCH_NOT_FOUND]:
    "This invitation link isn't valid. Ask Claude to prepare the invitations again.",
  [ERROR_CODES.INVITATION_BATCH_EXPIRED]: 'This invitation link has expired.',
  [ERROR_CODES.INVITATION_BATCH_NOT_PENDING]:
    'These invitations were already sent or cancelled, so nothing more was sent.',
  [ERROR_CODES.INVITATION_BATCH_DAILY_LIMIT]: INVITATION_BATCH_DAILY_LIMIT_MESSAGE,
  [ERROR_CODES.INVITATION_BATCH_FORBIDDEN]:
    'Only the person who prepared these invitations can send them, signed in as themselves.',
  // ENG-5322. The api refuses the booking but has already emailed the expert, so
  // this is an outcome to explain, not a failure to retry: a retry sends another email.
  [ERROR_CODES.CONSULTATION_UNREGISTERED_EXPERT_REQUEST]: UNREGISTERED_EXPERT_REQUEST_MESSAGE,
  [ERROR_CODES.BILLING_INVALID_TRIAL_DAYS]:
    "Your trial offer isn't available right now. Please continue without it.",
  [ERROR_CODES.BILLING_IDEMPOTENCY_IN_PROGRESS]:
    "We're still finalizing your previous attempt. Give us a moment.",
  [ERROR_CODES.BILLING_IDEMPOTENCY_CONFLICT]:
    'Your previous attempt used different details. Please refresh and try again.',
  [ERROR_CODES.BILLING_INVALID_IDEMPOTENCY_KEY]:
    'Something went wrong with this attempt. Please refresh and try again.',
  [ERROR_CODES.BILLING_STRIPE_CUSTOMER_INVALID]:
    "We couldn't process your payment details. Please try again or contact support.",
  [ERROR_CODES.BILLING_PRICING_UNAVAILABLE]:
    "We couldn't load subscription pricing. Refresh the page and try again.",
  // The booking surfaces block submit when a rate is already known to be missing, but the
  // rate can go unpriceable between page load and submit — without these, the raw GraphQL
  // string reaches the user (ENG-5571).
  // All four are the family raised behind a null client_rate / credit_rate: the take-rate pair
  // from CalculateClientRateByUserID, the credit-pricing pair from CalculateCreditRateByBillRate
  // (expert/service/expert_service.go:46,53). The account-scoped and site-scoped variants differ
  // only in which config is missing, which is not something the user can act on differently.
  [ERROR_CODES.BILLING_TAKE_RATE_UNAVAILABLE]:
    "We can't price this expert right now, so this booking couldn't be completed. Please contact support.",
  [ERROR_CODES.BILLING_SITE_TAKE_RATE_UNAVAILABLE]:
    "We can't price this expert right now, so this booking couldn't be completed. Please contact support.",
  [ERROR_CODES.BILLING_INVALID_CREDIT_PRICING]:
    "We can't price this expert right now, so this booking couldn't be completed. Please contact support.",
  [ERROR_CODES.BILLING_INVALID_SITE_CREDIT_PRICING]:
    "We can't price this expert right now, so this booking couldn't be completed. Please contact support.",
  // BILLING_SIGNUP_FAILED is intentionally without a friendly default. It's
  // used as a fallback code when the underlying error has no extensions.code,
  // and the call site (SubscriptionStep) prefers the raw err.message in that
  // case so we surface the actual failure reason rather than a generic line.
  [ERROR_CODES.AUTH_ACCOUNT_EXISTS_LOGIN_REQUIRED]: 'An account with this email already exists',
  [ERROR_CODES.AUTH_CREDENTIALS_MISSING]: CREDENTIALS_MISSING_MESSAGE,
  [ERROR_CODES.AUTH_EMAIL_REQUIRED]:
    "We couldn't verify an email address on your account. Please contact support.",
  [ERROR_CODES.AUTH_SEND_OTP_FAILED]:
    "We couldn't send your verification code. Please try again in a moment.",
  [ERROR_CODES.AUTH_OTP_TOKEN_EXPIRED]:
    'Your verification session expired. Please request a new code.',
  [ERROR_CODES.AUTH_MFA_ENROLLMENT_REQUIRED]:
    'Your account needs multi-factor authentication set up before you can continue. Sign in to finish setup.',
  [ERROR_CODES.GROUP_NOT_JOINABLE]: "This team isn't accepting new members right now.",
  [ERROR_CODES.GROUP_ALREADY_MEMBER_OR_PENDING]:
    "You've already requested access. Check your email.",
  [ERROR_CODES.GROUP_EMAIL_DOMAIN_MISMATCH]:
    "Your email domain doesn't match this team. Ask the admin to invite you.",
  // Engagement validation codes (ENG-5500). The user-facing flows (offer, governance,
  // hours, messaging, state gates, min-hours/rate validation) carry crafted copy;
  // guard-rail codes a normal user shouldn't hit carry the API's message verbatim.
  [ERROR_CODES.ENGAGEMENT_ACTOR_REQUIRED]: 'engagement mutation requires an authenticated actor',
  [ERROR_CODES.ENGAGEMENT_AMENDMENT_ALREADY_OPEN]:
    'This engagement already has an open amendment. Resolve it before proposing another.',
  [ERROR_CODES.ENGAGEMENT_AMENDMENT_INVALID_CHANGE_SET]:
    'Change at least one of the not-to-exceed hours, expert rate, or scope to propose an amendment.',
  [ERROR_CODES.ENGAGEMENT_AMENDMENT_NOT_ACTIONABLE]: 'This amendment has already been resolved.',
  [ERROR_CODES.ENGAGEMENT_AMENDMENT_NOT_COUNTERPARTY]:
    'Only the other party can act on this proposal.',
  [ERROR_CODES.ENGAGEMENT_AMENDMENT_NOT_FOUND]: 'amendment not found',
  [ERROR_CODES.ENGAGEMENT_AMENDMENT_NOT_PARTICIPANT]:
    'Only the expert or a work approver can amend this engagement.',
  [ERROR_CODES.ENGAGEMENT_AMENDMENT_NOT_PROPOSER]: 'Only the proposer can withdraw this amendment.',
  [ERROR_CODES.ENGAGEMENT_AMENDMENT_NTE_BELOW_CONFIRMED]:
    "The amended not-to-exceed hours can't be below the hours already confirmed.",
  [ERROR_CODES.ENGAGEMENT_AMENDMENT_PRICING_CONFIG_MISSING]:
    "This engagement's pricing must be refreshed before it can be amended. Re-send the offer, then try again.",
  // ENG-5727 create authority. These are the LAST-RESORT copy: both hire entry
  // points prefer `createDenialMessageForCode`
  // (features/consulting-engagements/live/create-denial.ts), which names the
  // account or project the viewer is missing and who can add them. These
  // name-free versions are what any other consumer of the code would show, so
  // they still have to say what to do rather than only that something failed.
  [ERROR_CODES.ENGAGEMENT_ACCOUNT_NOT_ENTITLED]:
    'This project\u2019s account does not have engagements enabled. Contact OnFrontiers to add it.',
  [ERROR_CODES.ENGAGEMENT_ACTOR_NOT_GROUP_MEMBER]:
    'You are not a member of this project\u2019s account. Being on the project alone does not allow creating an engagement \u2014 ask an account admin to add you.',
  [ERROR_CODES.ENGAGEMENT_ACTOR_NOT_PROJECT_MEMBER]:
    'You are not a member of this project. Ask a project owner to add you, or ask an account owner to create the engagement.',
  [ERROR_CODES.ENGAGEMENT_ACTOR_NOT_GROUP_OR_PROJECT_MEMBER]:
    'You are not a member of this project or its account. Ask an account admin to add you.',
  [ERROR_CODES.ENGAGEMENT_CLOSE_HOURS_PENDING]:
    'Confirm or dispute the outstanding hours before closing this engagement.',
  [ERROR_CODES.ENGAGEMENT_CLOSE_HOURS_UNBILLED]:
    'Confirmed hours are still awaiting billing. Close this engagement once they are invoiced.',
  [ERROR_CODES.ENGAGEMENT_CLOSE_PERIOD_UNRESOLVED]:
    'A billing period is still settling. Close this engagement once it resolves.',
  [ERROR_CODES.ENGAGEMENT_AWAITING_MISMATCH]:
    "This engagement isn't awaiting that action. Refresh the page to see its current status.",
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_ACTIVE_ENGAGEMENTS_EXIST]:
    'account has engagements in a non-terminal state',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_ALREADY_ENABLED]:
    'Engagements are already enabled for this account.',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_INVALID_ALERT_PCT]: 'nte alert pct must be NULL or 1-99',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_INVALID_FUNDING_METHOD]:
    'unknown over-threshold funding method',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_INVALID_GOVERNANCE_THRESHOLD]:
    'governance threshold credits must be >= 0',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_INVALID_INVOICE_TERMS]: 'invoice terms days must be > 0',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_INVALID_MIN_HOURS]:
    'Minimum engagement hours must be greater than zero.',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_INVALID_POLICY_TYPE]: 'unknown governance policy type',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_INVALID_TAKE_RATES]:
    'take rates must satisfy 0 <= long_term <= reduced <= base <= 100',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_INVALID_THRESHOLDS]:
    'bracket boundaries must satisfy 0 < reduced_threshold_hours < long_term_threshold_hours',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_INVALID_TIMER_DAYS]:
    'timer days must be NULL or a positive integer',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_NO_BILLING_ACCOUNT]:
    "project's team has no billing account",
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_NO_TEAM]: 'project has no team',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_NOT_ENABLED]:
    'Engagements are not enabled for this account.',
  [ERROR_CODES.ENGAGEMENT_BILLING_SETTINGS_PLAN_NOT_ELIGIBLE]:
    'This account plan is not eligible for engagements.',
  [ERROR_CODES.ENGAGEMENT_CHANNEL_FORBIDDEN]:
    "You don't have permission to send messages in this channel.",
  [ERROR_CODES.ENGAGEMENT_CHANNEL_NOT_FOUND]: 'engagement message channel not found',
  [ERROR_CODES.ENGAGEMENT_CHANNEL_TYPE_INVALID]: 'unknown or unsupported engagement channel type',
  [ERROR_CODES.ENGAGEMENT_CHANNEL_UNAVAILABLE]:
    'Engagement messaging is temporarily unavailable. Try again shortly.',
  // ENG-5746. Deliberately says nothing about trying again: this is the code
  // the api reserves for a Sendbird refusal to provision the sender's user
  // record, which no retry clears. The bug that produced the ticket was a
  // permanent failure wearing the line above, so a participant retried
  // forever. Anything genuinely transient still answers
  // ENGAGEMENT_CHANNEL_UNAVAILABLE and still reads "try again shortly".
  [ERROR_CODES.ENGAGEMENT_MESSAGING_SETUP_FAILED]:
    "We couldn't set up messaging for your account. Please contact support.",
  [ERROR_CODES.CONVERSATION_MESSAGING_SETUP_FAILED]:
    "We couldn't set up messaging for your account. Please contact support.",
  [ERROR_CODES.ENGAGEMENT_DUPLICATE_PRIMARY_ALLOCATION]:
    'engagement already has a live primary allocation',
  [ERROR_CODES.ENGAGEMENT_NOT_ACTIVE]:
    'Hours can only be submitted while the engagement is active.',
  [ERROR_CODES.ENGAGEMENT_EXPERT_PAYOUTS_NOT_ENABLED]:
    'Set up your payout account before sending an offer.',
  [ERROR_CODES.ENGAGEMENT_EXPERT_RATE_INVALID]: 'Enter an hourly rate greater than zero.',
  [ERROR_CODES.ENGAGEMENT_EXPERT_REQUIRED]:
    'engagement requires an expert unless created via customer_introduced',
  [ERROR_CODES.ENGAGEMENT_EXPERT_UNRESOLVABLE]:
    'engagement creation source has no resolvable platform expert',
  [ERROR_CODES.ENGAGEMENT_GOVERNANCE_APPROVER_REQUIRED]:
    'A governance-required offer needs at least one governance approver.',
  [ERROR_CODES.ENGAGEMENT_GOVERNANCE_INVALID_DECISION]: 'unknown or malformed governance decision',
  [ERROR_CODES.ENGAGEMENT_GOVERNANCE_INVALID_NTE]:
    'The proposed not-to-exceed total must be a positive dollar amount.',
  [ERROR_CODES.ENGAGEMENT_GOVERNANCE_NOTE_REQUIRED]: 'A note is required when rejecting an offer.',
  [ERROR_CODES.ENGAGEMENT_GOVERNANCE_NOTE_TOO_LONG]:
    'Governance notes must be 255 characters or fewer.',
  [ERROR_CODES.ENGAGEMENT_HOURS_ACTOR_NOT_EXPERT]: 'Only the engagement expert can submit hours.',
  [ERROR_CODES.ENGAGEMENT_HOURS_ACTOR_NOT_WORK_APPROVER]:
    'Only a work approver can confirm, dispute, or resolve hours.',
  [ERROR_CODES.ENGAGEMENT_HOURS_INVALID]:
    'Enter a valid number of hours — a positive amount with at most two decimal places.',
  [ERROR_CODES.ENGAGEMENT_INVALID_CONFIRM_MODE]:
    'confirm mode must be one of full, partial, dispute',
  [ERROR_CODES.ENGAGEMENT_INVALID_CONFIRMED_HOURS]:
    'Confirmed hours must be greater than zero and less than the hours submitted.',
  [ERROR_CODES.ENGAGEMENT_INVALID_CREATION_PATH]: 'unknown or unsupported engagement creation path',
  [ERROR_CODES.ENGAGEMENT_INVALID_DATES]: 'The end date must not be before the start date.',
  [ERROR_CODES.ENGAGEMENT_INVALID_ESTIMATED_HOURS]:
    'Estimated hours must be greater than zero and within the supported maximum.',
  [ERROR_CODES.ENGAGEMENT_INVALID_RESOLVE_OUTCOME]:
    'resolution outcome must be one of confirmed, partially_confirmed, rejected, withdrawn',
  [ERROR_CODES.ENGAGEMENT_INVALID_SOURCE_REF]:
    'engagement creation input has missing, extraneous, or disallowed source references',
  [ERROR_CODES.ENGAGEMENT_INVALID_TRANSITION]:
    "This action isn't available in the engagement's current state. Refresh the page and try again.",
  [ERROR_CODES.ENGAGEMENT_MESSAGE_INVALID]: 'Messages need text or an attachment.',
  [ERROR_CODES.ENGAGEMENT_NOT_FOUND]: 'engagement not found',
  [ERROR_CODES.ENGAGEMENT_NOT_GOVERNANCE_APPROVER]:
    'Only a governance approver can review this offer.',
  [ERROR_CODES.ENGAGEMENT_NOTE_REQUIRED]:
    'A note is required for partial confirmation, dispute, or dispute resolution.',
  [ERROR_CODES.ENGAGEMENT_NOTE_TOO_LONG]: 'Notes must be 255 characters or fewer.',
  [ERROR_CODES.ENGAGEMENT_NOTIFICATION_CLASS_INVALID]: 'unknown engagement notification class',
  [ERROR_CODES.ENGAGEMENT_NTE_EXCEEDED]:
    "These hours would exceed the engagement's not-to-exceed limit.",
  [ERROR_CODES.ENGAGEMENT_NTE_HOURS_BELOW_MINIMUM]:
    "The not-to-exceed hours are below your account's minimum for engagements.",
  // The six refusals of the ENG-5630 operator retry. Staff-only copy: the only
  // caller is an OF operator on the receivables queue, so these say what to do
  // next rather than apologising to a customer.
  [ERROR_CODES.ENGAGEMENT_PERIOD_NOT_FAILED]:
    'This billing period is settled, skipped or voided, so there is nothing to retry.',
  // Distinct from NOT_FAILED on purpose: that one means the row exists and is
  // already resolved, this one means the id is wrong. Saying "not in a failed
  // state" for a row that does not exist sends the operator to look at a state
  // it does not have.
  [ERROR_CODES.ENGAGEMENT_PERIOD_NOT_FOUND]:
    'No billing period with that id. Check the id and try again.',
  [ERROR_CODES.ENGAGEMENT_PERIOD_NOT_RETRYABLE]:
    "This period's failure cannot be cleared by retrying — its pricing snapshot was " +
    'frozen when the offer was made.',
  // Not an error in substance: the guard worked and exactly one retry ran.
  // Surface it as an explanation, never as a red failure.
  [ERROR_CODES.ENGAGEMENT_PERIOD_RETRY_LOST]:
    'Someone else retried this period first. Refresh to see the result.',
  [ERROR_CODES.ENGAGEMENT_RETRY_NOTE_REQUIRED]:
    'Add a note explaining why this period is being retried.',
  [ERROR_CODES.ENGAGEMENT_RETRY_SETTINGS_NOT_READY]:
    'This account still has no billing settings or billing email. Set them before retrying.',
  [ERROR_CODES.ENGAGEMENT_PROJECT_ACCOUNT_MISMATCH]:
    'engagement project has no derivable billing account',
  [ERROR_CODES.ENGAGEMENT_PROJECT_SETTINGS_INVALID_OVERRIDE]:
    'override threshold credits must be >= 0',
  [ERROR_CODES.ENGAGEMENT_PREVIEW_RATE_UNAVAILABLE]:
    "We couldn't estimate a price for this expert because they have no rate set.",
  [ERROR_CODES.ENGAGEMENT_RESOLUTION_TOO_LONG]:
    'Dispute resolution notes must be 255 characters or fewer.',
  [ERROR_CODES.ENGAGEMENT_SCOPE_TOO_LONG]: 'Scope descriptions must be 2000 characters or fewer.',
  [ERROR_CODES.ENGAGEMENT_SOURCE_NOT_FOUND]: 'engagement creation source record was not found',
  [ERROR_CODES.ENGAGEMENT_SUBMISSION_NOT_ESCALATED]: 'hours submission is not in escalated status',
  [ERROR_CODES.ENGAGEMENT_SUBMISSION_NOT_FOUND]: 'hours submission not found',
  [ERROR_CODES.ENGAGEMENT_SUBMISSION_NOT_SUBMITTED]: 'hours submission is not in submitted status',
  [ERROR_CODES.ENGAGEMENT_TAKE_RATE_INVALID]: 'take rate must be at least 0% and below 100%',
  [ERROR_CODES.ENGAGEMENT_TRANSITION_FROM_TERMINAL]:
    'This engagement has ended and can no longer be changed.',
  [ERROR_CODES.ENGAGEMENT_WORK_APPROVER_NOT_OWNER]:
    'work approver must be an owner of the engagement project',
  [ERROR_CODES.ENGAGEMENT_WORK_DESCRIPTION_REQUIRED]:
    'A work description is required when submitting hours.',
  // Expert-payment (XP) codes (ENG-5485). The operator settlement surface is
  // internal-staff-only, so those codes carry the API message verbatim; the
  // three a user can hit on the payout dashboard get crafted copy.
  [ERROR_CODES.EXPERT_PAYMENT_NOT_CLAIMABLE]: 'payment is not in a claimable state',
  [ERROR_CODES.EXPERT_PAYMENT_PAYOUTS_NOT_ENABLED]:
    "Payouts aren't enabled for this account yet. Finish setting up your payout account, then try again.",
  [ERROR_CODES.EXPERT_PAYMENT_EXECUTION_DISABLED]: 'expert payment execution is disabled',
  [ERROR_CODES.EXPERT_PAYMENT_DUPLICATE_LIVE_PAYMENT]:
    'a live payment already exists for this source and kind',
  [ERROR_CODES.EXPERT_PAYMENT_AMOUNT_IMMUTABLE]: 'payment amount cannot be changed after creation',
  [ERROR_CODES.EXPERT_PAYMENT_INVALID_INPUT]: 'invalid expert payment input',
  [ERROR_CODES.EXPERT_PAYMENT_NOT_FOUND]: 'expert payment not found',
  // Raised with validation.Errorf, so the API message interpolates the row's
  // actual age and the configured cutoff; this static default drops both.
  [ERROR_CODES.EXPERT_PAYMENT_TOO_FRESH]:
    'cannot force-resolve: the payment was claimed too recently and its executor may still be mid-transfer; retry later, or use resolve to adopt an existing transfer',
  [ERROR_CODES.PAYOUT_ACCOUNT_NOT_FOUND]:
    "You haven't set up a payout account yet. Set one up to receive payouts.",
  [ERROR_CODES.PAYOUT_COUNTRY_UNSUPPORTED]:
    "Payouts aren't supported for your country yet. Contact support for help.",
  // Names the field and where to set it. Surfaces that can render a link point
  // at /settings/location instead of spelling the path out; this is the copy
  // for the ones that cannot.
  [ERROR_CODES.PAYOUT_COUNTRY_REQUIRED]:
    'Add your country under Settings > Location, then set up payouts.',
  // Raised with validation.Errorf, so the API message names the field that was
  // refused ("Your phone number could not be accepted..."). This static default
  // drops that, so prefer the server's message wherever one is available.
  [ERROR_CODES.PAYOUT_SETUP_REJECTED]:
    "Something in your profile couldn't be accepted for payout setup. Check your details and try again.",
  [ERROR_CODES.PAYOUT_SETUP_RETRY_LATER]:
    'Payout setup is still finishing an earlier attempt. Try again in a few minutes.',
  // Word-for-word the settings route's long-standing `payout_setup_failed`
  // copy, so the drawer and the toast do not describe one failure two ways.
  [ERROR_CODES.PAYOUT_SETUP_FAILED]:
    'Something went wrong setting up your payout account. Please try again or contact support.',
  // Paging codes carry the API message verbatim: every surface that can raise
  // one is internal-staff-only today, and the call sites that show a user
  // anything (the AP console's queue and ledger) write their own copy against
  // the code rather than reading these.
  [ERROR_CODES.PAGING_INVALID_ARGUMENT]: "'first' argument must be between 1 and 1000",
  [ERROR_CODES.PAGING_CURSOR_INVALID]: 'invalid cursor',
};

export function getDefaultErrorMessage(code: string): string | undefined {
  return ERROR_CODE_DEFAULT_MESSAGES[code as ErrorCode];
}
